/**
 * 媒體報表匯出共用工具（Docx / ZIP / PDF）
 */

import { normalizeStatusForDisplay } from './mediaGrouping';

export const getImageUrl = (filePath) => {
  if (!filePath) return '';

  if (
    filePath.startsWith('http://') ||
    filePath.startsWith('https://') ||
    filePath.startsWith('data:')
  ) {
    return filePath;
  }

  if (filePath.startsWith('/')) {
    return filePath;
  }

  return `/Uploads/${filePath}`;
};

export const sanitizePathSegment = (name, maxLen = 80) => {
  let s = String(name ?? 'unknown')
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) s = 'unknown';
  if (s.length > maxLen) s = s.slice(0, maxLen);
  return s;
};

export const getFileBaseName = (filePath) => {
  if (!filePath) return 'image';
  const normalized = filePath.replace(/\\/g, '/');
  const base = normalized.split('/').pop() || 'image';
  return sanitizePathSegment(base, 120);
};

/**
 * 取得圖片二進位與尺寸（供 Docx / ZIP 使用）
 */
export const fetchImageData = async (filePath) => {
  const url = getImageUrl(filePath);
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${url}`);
  }
  const blob = await response.blob();
  const buffer = await blob.arrayBuffer();
  const type = (blob.type || '').toLowerCase();
  let width = 400;
  let height = 300;

  try {
    const dims = await getImageDimensionsFromBuffer(buffer, type);
    width = dims.width;
    height = dims.height;
  } catch {
    // 無法讀取尺寸時使用預設
  }

  const ext = getImageExtension(filePath, type);
  return { buffer, width, height, ext, mimeType: type };
};

const getImageDimensionsFromBuffer = (buffer, mimeType) =>
  new Promise((resolve, reject) => {
    const blob = new Blob([buffer], { type: mimeType || 'image/jpeg' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('無法讀取圖片尺寸'));
    };
    img.src = url;
  });

export const getImageExtension = (filePath, mimeType = '') => {
  const pathExt = (filePath || '').split('.').pop()?.toLowerCase();
  if (pathExt && ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(pathExt)) {
    return pathExt === 'jpeg' ? 'jpg' : pathExt;
  }
  if (mimeType.includes('png')) return 'png';
  if (mimeType.includes('gif')) return 'gif';
  if (mimeType.includes('webp')) return 'webp';
  if (mimeType.includes('bmp')) return 'bmp';
  return 'jpg';
};

export const getPvDisplayLabel = (variableName, pvDefinitions = []) => {
  if (!variableName) return '';
  const def = pvDefinitions.find(
    (pv) => pv.variableName === variableName || pv.VariableName === variableName
  );
  return def?.displayName || def?.DisplayName || variableName;
};

export const buildPvGroupTitle = (group, groupingPvs, pvDefinitions = []) => {
  if (!groupingPvs?.length || !group?.processVariables) {
    return null;
  }
  const pvInfo = groupingPvs
    .filter((key) => {
      const value = group.processVariables[key];
      return value !== null && value !== undefined;
    })
    .map((key) => `${getPvDisplayLabel(key, pvDefinitions)}: ${group.processVariables[key]}`)
    .join(', ');
  return pvInfo ? `分組的流程變量: ${pvInfo}` : null;
};

/**
 * 建立完整分組標題（PV + eForm 欄位合併），供 PDF / Docx 匯出使用。
 * 回傳格式範例：「房屋名稱: 彭半山 | 工作內容: 2座天台食水A缸」
 * @param {Object} group
 * @param {Array}  groupingPvs
 * @param {Array}  pvDefinitions
 * @param {Array}  groupingEformFields  [{ eformName, fieldId, displayLabel }]
 * @returns {string|null}
 */
export const buildFullGroupTitle = (group, groupingPvs = [], pvDefinitions = [], groupingEformFields = []) => {
  const parts = [];

  // 流程資料格部分
  if (groupingPvs.length && group?.processVariables) {
    groupingPvs.forEach(key => {
      const value = group.processVariables[key];
      if (value !== null && value !== undefined) {
        parts.push(`${getPvDisplayLabel(key, pvDefinitions)}: ${value}`);
      }
    });
  }

  // eForm 欄位部分
  if (groupingEformFields.length && group?.eformValues) {
    groupingEformFields.forEach(({ eformName, fieldId, displayLabel }) => {
      const val = group.eformValues[`${eformName}:${fieldId}`];
      if (val !== null && val !== undefined) {
        parts.push(`${displayLabel || fieldId}: ${val}`);
      }
    });
  }

  return parts.length ? parts.join(' | ') : null;
};

export const buildGroupFolderName = (group, groupingPvs, groupIndex, pvDefinitions = [], groupingEformFields = []) => {
  const parts = [];

  // PV 分組：只使用值
  if (groupingPvs?.length && group?.processVariables) {
    for (const key of groupingPvs) {
      const value = group.processVariables[key];
      if (value !== null && value !== undefined) {
        parts.push(`${value}`);
      }
    }
  }

  // eForm 分組：只使用值
  if (groupingEformFields?.length && group?.eformValues) {
    for (const { eformName, fieldId } of groupingEformFields) {
      const key = `${eformName}:${fieldId}`;
      const value = group.eformValues[key];
      if (value !== null && value !== undefined) {
        parts.push(`${value}`);
      }
    }
  }

  if (parts.length) {
    return sanitizePathSegment(parts.join('__'));
  }

  return sanitizePathSegment(`Group_${groupIndex + 1}`);
};

export const buildStatusSubGroupTitle = (subGroup) => {
  const normalizedStatus = normalizeStatusForDisplay(subGroup.status);
  const execCount = subGroup.executions?.length || 0;
  const execId = subGroup.workflowExecution?.id;
  let suffix = '';
  if (execCount > 1) {
    suffix = ` (${execCount} 個實例)`;
  } else if (execId) {
    suffix = ` (ID: ${execId})`;
  }
  return `狀態: ${normalizedStatus}${suffix}`;
};

export const buildStatusFolderName = (subGroup) => {
  const normalizedStatus = normalizeStatusForDisplay(subGroup.status);
  return sanitizePathSegment(`Status_${normalizedStatus}`);
};

const MM_TO_PX = 96 / 25.4;
const DOCX_MARGIN_MM = 10;

/** 與 PDF 一致的可用內容寬度（px）與欄數 */
export const getDocxLayoutMetrics = (settings = {}) => {
  const orientation = settings.pdfOrientation || settings.orientation || 'landscape';
  const maxImagesPerPage = Math.max(1, Math.min(Number(settings.maxImagesPerPage) || 4, 12));
  const imageGap = Number(settings.imageGap) || 5;
  const minImageWidthMm = Number(settings.minImageWidth) || 50;

  const pageWidthMm = orientation === 'landscape' ? 297 : 210;
  const availableWidthMm = pageWidthMm - DOCX_MARGIN_MM * 2;
  const cellMaxWidthMm = (availableWidthMm - (maxImagesPerPage - 1) * imageGap) / maxImagesPerPage;
  const cellMaxWidthPx = Math.round(cellMaxWidthMm * MM_TO_PX);
  const minImageWidthPx = Math.round(Math.min(minImageWidthMm, cellMaxWidthMm) * MM_TO_PX);

  return {
    orientation,
    cols: maxImagesPerPage,
    cellMaxWidthPx,
    minImageWidthPx,
  };
};

/** 將圖片縮放至欄寬（與 PDF calculateImageSize 邏輯一致） */
export const scaleImageForDocxCell = (width, height, cellMaxWidthPx, minImageWidthPx = 0) => {
  const ratio = width / height;
  let finalWidth = cellMaxWidthPx;
  let finalHeight = cellMaxWidthPx / ratio;

  if (minImageWidthPx > 0 && finalWidth < minImageWidthPx) {
    finalWidth = Math.min(minImageWidthPx, cellMaxWidthPx);
    finalHeight = finalWidth / ratio;
  }

  return {
    width: Math.round(finalWidth),
    height: Math.round(finalHeight),
  };
};

/** 將不支援 Docx 的格式（如 webp）轉為 JPEG */
export const ensureDocxCompatibleImage = async (filePath) => {
  const data = await fetchImageData(filePath);
  if (data.ext !== 'webp') {
    return data;
  }
  const jpegBuffer = await convertImageBufferToJpeg(data.buffer, data.mimeType);
  return {
    ...data,
    buffer: jpegBuffer,
    ext: 'jpg',
  };
};

const convertImageBufferToJpeg = (buffer, mimeType) =>
  new Promise((resolve, reject) => {
    const blob = new Blob([buffer], { type: mimeType || 'image/webp' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (jpegBlob) => {
          URL.revokeObjectURL(url);
          if (!jpegBlob) {
            reject(new Error('WebP 轉 JPEG 失敗'));
            return;
          }
          jpegBlob.arrayBuffer().then(resolve).catch(reject);
        },
        'image/jpeg',
        0.92
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('無法載入 WebP 圖片'));
    };
    img.src = url;
  });

export const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};
